import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { 
  BarChart3, TrendingUp, TrendingDown, Activity, LineChart as LineChartIcon,
  Calendar as CalendarIcon, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, ComposedChart, Bar, Area, ReferenceLine
} from "recharts";
import api from "@/lib/api";

const ICONS = {
  cocoa: "🍫", coffee: "☕", sugar: "🍬", cotton: "🧵", wheat: "🌾", 
  maize: "🌽", soybean: "🫘", palm_oil: "🌴", gold: "🥇", crude_oil: "🛢️",
  copper: "🔶", natural_gas: "🔥", silver: "🥈", zinc: "⚪"
};

export default function HistoricalPage() {
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState("gold");
  const [period, setPeriod] = useState("30d");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [historicalData, setHistoricalData] = useState(null);
  const [compareWith, setCompareWith] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("price");

  const fetchCommodities = useCallback(async () => {
    try {
      const res = await api.get("/price-alerts/commodities");
      setCommodities(res.data.commodities || []);
    } catch (err) {
      console.error("Error fetching commodities:", err);
    }
  }, []);

  const fetchHistoricalData = useCallback(async () => {
    if (!selectedCommodity) return;
    
    setLoading(true);
    try {
      const params = { indicators: "sma,ema,rsi,macd" };
      
      if (period === "custom" && customDateRange.from && customDateRange.to) {
        params.period = "custom";
        params.start_date = customDateRange.from.toISOString();
        params.end_date = customDateRange.to.toISOString();
      } else {
        params.period = period;
      }
      
      const res = await api.get(`/historical/${selectedCommodity}`, { params });
      setHistoricalData(res.data);
    } catch (err) {
      console.error("Error fetching historical data:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCommodity, period, customDateRange]);

  const fetchComparisonData = useCallback(async () => {
    if (!selectedCommodity || compareWith.length === 0) {
      setComparisonData(null);
      return;
    }
    
    try {
      const res = await api.get(`/historical/${selectedCommodity}/compare`, {
        params: {
          compare_with: compareWith.join(","),
          period
        }
      });
      setComparisonData(res.data);
    } catch (err) {
      console.error("Error fetching comparison data:", err);
    }
  }, [selectedCommodity, compareWith, period]);

  useEffect(() => {
    fetchCommodities();
  }, [fetchCommodities]);

  useEffect(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  useEffect(() => {
    fetchComparisonData();
  }, [fetchComparisonData]);

  const toggleCompare = (commodityId) => {
    if (commodityId === selectedCommodity) return;
    setCompareWith(prev => 
      prev.includes(commodityId) 
        ? prev.filter(c => c !== commodityId)
        : [...prev, commodityId].slice(0, 3) // Max 3 comparisons
    );
  };

  const currentCommodity = commodities.find(c => c.id === selectedCommodity);
  const stats = historicalData?.statistics || {};
  const prices = historicalData?.prices || [];

  // Prepare RSI data
  const rsiData = prices.map(p => ({
    date: p.date,
    rsi: p.rsi,
    overbought: 70,
    oversold: 30
  }));

  // Prepare MACD data
  const macdData = prices.map(p => ({
    date: p.date,
    macd: p.macd,
    signal: p.macd_signal,
    histogram: p.macd_histogram
  }));

  // Prepare comparison data for chart
  const comparisonChartData = comparisonData 
    ? Object.keys(comparisonData.commodities).reduce((acc, cid, idx) => {
        const data = comparisonData.commodities[cid].data;
        data.forEach((point, i) => {
          if (!acc[i]) acc[i] = { date: point.date };
          acc[i][cid] = point.value;
        });
        return acc;
      }, [])
    : [];

  const COLORS = ["#00ff41", "#3b82f6", "#f59e0b", "#ef4444"];

  return (
    <div data-testid="historical-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Historical Analysis
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Technical analysis with indicators
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedCommodity} onValueChange={setSelectedCommodity}>
            <SelectTrigger className="w-40 rounded-none border-zinc-700 bg-zinc-950">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
              {commodities.map(c => (
                <SelectItem key={c.id} value={c.id} className="font-mono">
                  {ICONS[c.id]} {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={period} onValueChange={(val) => {
            setPeriod(val);
            if (val !== "custom") {
              setShowDatePicker(false);
            }
          }}>
            <SelectTrigger className="w-28 rounded-none border-zinc-700 bg-zinc-950">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
              <SelectItem value="7d">7 Days</SelectItem>
              <SelectItem value="30d">30 Days</SelectItem>
              <SelectItem value="90d">90 Days</SelectItem>
              <SelectItem value="1y">1 Year</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          
          {period === "custom" && (
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-none border-zinc-700 bg-zinc-950 text-zinc-300 hover:bg-zinc-900"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {customDateRange.from && customDateRange.to ? (
                    <span className="text-xs font-mono">
                      {format(customDateRange.from, "MMM dd")} - {format(customDateRange.to, "MMM dd")}
                    </span>
                  ) : (
                    <span className="text-xs">Select dates</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="end">
                <Calendar
                  mode="range"
                  selected={customDateRange}
                  onSelect={(range) => {
                    setCustomDateRange(range || { from: null, to: null });
                    if (range?.from && range?.to) {
                      setShowDatePicker(false);
                    }
                  }}
                  numberOfMonths={2}
                  className="bg-zinc-900"
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Current Price Banner */}
      {currentCommodity && (
        <Card className="bg-gradient-to-r from-zinc-900 to-zinc-950 border-zinc-800 rounded-none">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-4xl">{ICONS[selectedCommodity]}</span>
              <div>
                <h2 className="font-heading text-xl text-zinc-100">{currentCommodity.name}</h2>
                <p className="text-xs text-zinc-500 font-mono">{currentCommodity.unit}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-mono font-bold text-zinc-100">
                ${currentCommodity.price.toLocaleString()}
              </p>
              <div className={`flex items-center justify-end gap-1 text-sm font-mono ${currentCommodity.change >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                {currentCommodity.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {currentCommodity.change >= 0 ? '+' : ''}{currentCommodity.change.toFixed(2)}%
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Period High</p>
            <p className="text-lg font-mono font-bold text-[#00ff41]">${stats.high?.toLocaleString() || "-"}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Period Low</p>
            <p className="text-lg font-mono font-bold text-[#ff3b30]">${stats.low?.toLocaleString() || "-"}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Average</p>
            <p className="text-lg font-mono font-bold text-zinc-100">${stats.average?.toLocaleString() || "-"}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Change</p>
            <p className={`text-lg font-mono font-bold ${(stats.change || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
              {(stats.change || 0) >= 0 ? '+' : ''}${stats.change?.toLocaleString() || "0"}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Return</p>
            <p className={`text-lg font-mono font-bold ${(stats.change_pct || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
              {(stats.change_pct || 0) >= 0 ? '+' : ''}{stats.change_pct?.toFixed(2) || "0"}%
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Volatility</p>
            <p className="text-lg font-mono font-bold text-zinc-100">${stats.volatility?.toFixed(2) || "-"}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none p-1">
          <TabsTrigger value="price" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <LineChartIcon className="w-3.5 h-3.5 mr-2" /> Price & MAs
          </TabsTrigger>
          <TabsTrigger value="rsi" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <Activity className="w-3.5 h-3.5 mr-2" /> RSI
          </TabsTrigger>
          <TabsTrigger value="macd" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <BarChart3 className="w-3.5 h-3.5 mr-2" /> MACD
          </TabsTrigger>
          <TabsTrigger value="compare" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <TrendingUp className="w-3.5 h-3.5 mr-2" /> Compare
          </TabsTrigger>
        </TabsList>

        <TabsContent value="price">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                Price Chart with Moving Averages
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {loading ? (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="animate-spin w-6 h-6 border-2 border-[#00ff41] border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={prices}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                      <YAxis stroke="#666" fontSize={10} domain={['auto', 'auto']} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 0 }}
                        labelStyle={{ color: '#888' }}
                      />
                      <Legend />
                      <Bar dataKey="volume" fill="#333" opacity={0.3} yAxisId="right" name="Volume" />
                      <Line type="monotone" dataKey="close" stroke="#00ff41" strokeWidth={2} dot={false} name="Price" />
                      <Line type="monotone" dataKey="sma_20" stroke="#3b82f6" strokeWidth={1} dot={false} name="SMA 20" strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="sma_50" stroke="#f59e0b" strokeWidth={1} dot={false} name="SMA 50" strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="ema_12" stroke="#8b5cf6" strokeWidth={1} dot={false} name="EMA 12" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rsi">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                  Relative Strength Index (RSI)
                </CardTitle>
                {rsiData.length > 0 && rsiData[rsiData.length - 1].rsi && (
                  <Badge className={`rounded-none ${
                    rsiData[rsiData.length - 1].rsi > 70 ? 'bg-[#ff3b30]/20 text-[#ff3b30]' :
                    rsiData[rsiData.length - 1].rsi < 30 ? 'bg-[#00ff41]/20 text-[#00ff41]' :
                    'bg-zinc-800 text-zinc-300'
                  }`}>
                    RSI: {rsiData[rsiData.length - 1].rsi?.toFixed(1)}
                    {rsiData[rsiData.length - 1].rsi > 70 ? ' (Overbought)' : 
                     rsiData[rsiData.length - 1].rsi < 30 ? ' (Oversold)' : ' (Neutral)'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rsiData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#666" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#666" fontSize={10} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 0 }}
                    />
                    <ReferenceLine y={70} stroke="#ff3b30" strokeDasharray="3 3" label={{ value: 'Overbought', fill: '#ff3b30', fontSize: 10 }} />
                    <ReferenceLine y={30} stroke="#00ff41" strokeDasharray="3 3" label={{ value: 'Oversold', fill: '#00ff41', fontSize: 10 }} />
                    <ReferenceLine y={50} stroke="#666" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="rsi" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="macd">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                MACD (12, 26, 9)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={macdData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#666" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#666" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 0 }}
                    />
                    <Legend />
                    <ReferenceLine y={0} stroke="#666" />
                    <Bar dataKey="histogram" name="Histogram" fill="#00ff41">
                      {macdData.map((entry, index) => (
                        <Bar key={index} fill={entry.histogram >= 0 ? '#00ff41' : '#ff3b30'} />
                      ))}
                    </Bar>
                    <Line type="monotone" dataKey="macd" stroke="#3b82f6" strokeWidth={2} dot={false} name="MACD" />
                    <Line type="monotone" dataKey="signal" stroke="#f59e0b" strokeWidth={2} dot={false} name="Signal" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compare">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                  Compare Performance
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  {commodities.filter(c => c.id !== selectedCommodity).map(c => (
                    <Button
                      key={c.id}
                      size="sm"
                      variant="outline"
                      className={`rounded-none text-xs font-mono ${
                        compareWith.includes(c.id) 
                          ? 'border-[#00ff41] bg-[#00ff41]/10 text-[#00ff41]' 
                          : 'border-zinc-700'
                      }`}
                      onClick={() => toggleCompare(c.id)}
                    >
                      {ICONS[c.id]} {c.name}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {compareWith.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-zinc-500 font-mono text-sm">Select commodities to compare</p>
                </div>
              ) : (
                <>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={comparisonChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" stroke="#666" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                        <YAxis stroke="#666" fontSize={10} tickFormatter={(v) => `${v}%`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 0 }}
                          formatter={(value) => [`${value.toFixed(2)}%`, '']}
                        />
                        <Legend />
                        <ReferenceLine y={0} stroke="#666" />
                        <Line type="monotone" dataKey={selectedCommodity} stroke={COLORS[0]} strokeWidth={2} dot={false} name={selectedCommodity.replace("_", " ").toUpperCase()} />
                        {compareWith.map((cid, idx) => (
                          <Line key={cid} type="monotone" dataKey={cid} stroke={COLORS[idx + 1]} strokeWidth={2} dot={false} name={cid.replace("_", " ").toUpperCase()} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {comparisonData && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      {[selectedCommodity, ...compareWith].map((cid, idx) => (
                        <div key={cid} className="bg-zinc-900 p-3 rounded-none border border-zinc-800">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                            <span className="font-mono text-xs text-zinc-400">{cid.replace("_", " ").toUpperCase()}</span>
                          </div>
                          <p className={`font-mono font-bold ${(comparisonData.commodities[cid]?.total_change || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                            {(comparisonData.commodities[cid]?.total_change || 0) >= 0 ? '+' : ''}
                            {comparisonData.commodities[cid]?.total_change?.toFixed(2) || 0}%
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
